import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Tab,
  Alert,
} from '@mui/material';
import Button from './Button';
import Card from './Card';
import Tabs from './Tabs'
import TextField from './TextField';
import {
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  ContentCopy as ContentCopyIcon,
  Help as HelpIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const JsonImportExport = ({
  data,
  onImport,
  title,
  description,
  jsonStructure = null,
}) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [showStructure, setShowStructure] = useState(false);
  const { t } = useTranslation(['courses', 'common']);

  const handleOpen = () => {
    setTab(0);
    setOpen(true);
    setError('');
    setJsonText('');
  };

  const handleClose = () => {
    setOpen(false);
    setShowStructure(false);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleImport = () => {
    try {
      if (!jsonText.trim()) {
        setError(t('courses:jsonImportExport.validation.emptyJson'));
        return;
      }
      const parsedData = JSON.parse(jsonText);
      onImport(parsedData);
      handleClose();
      toast.success(t('courses:jsonImportExport.success.imported'));
    } catch (err) {
      setError(t('courses:jsonImportExport.validation.invalidJson') + err.message);
    }
  };

  const handleExport = () => {
    const filename = `${title.toLowerCase().replace(/\s+/g, '_')}_${new Date()
      .toISOString()
      .slice(0, 10)}.json`;
    const exportText = JSON.stringify(data, null, 2);
    const blob = new Blob([exportText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('courses:jsonImportExport.success.exported'));
  };

  const copyToClipboard = () => {
    const exportText = JSON.stringify(data, null, 2);
    navigator.clipboard
      .writeText(exportText)
      .then(() => {
        toast.success(t('courses:jsonImportExport.success.copied'));
      })
      .catch((err) => {
        toast.error(t('courses:jsonImportExport.error.copying') + err.message);
      });
  };

  const toggleStructure = () => {
    setShowStructure(!showStructure);
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        setJsonText(content);
        JSON.parse(content);
        setError('');
      } catch (err) {
        setError(t('courses:jsonImportExport.validation.invalidJson') + err.message);
      }
    };
    reader.readAsText(file);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CodeIcon />}
        onClick={handleOpen}
        sx={{ ml: 1 }}
      >
        {t('courses:jsonImportExport.button')}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={2}>
            <CodeIcon
              color="primary"
              sx={{ fontSize: 28, display: { xs: 'none', md: 'block' } }}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('courses:jsonImportExport.title', { title })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description || t('courses:jsonImportExport.defaultDescription')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Tabs value={tab} onChange={handleTabChange} swapBackground>
              <Tab label={t('courses:jsonImportExport.tabs.export')} />
              <Tab label={t('courses:jsonImportExport.tabs.import')} />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Card>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Typography variant="subtitle1">
                  {t('courses:jsonImportExport.export.title')}
                </Typography>
                <Tooltip title={t('courses:jsonImportExport.export.copyTooltip')}>
                  <IconButton onClick={copyToClipboard}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                >
                  {t('courses:jsonImportExport.export.downloadButton')}
                </Button>
              </Stack>
              <TextField
                label={t('courses:jsonImportExport.export.jsonData')}
                multiline
                fullWidth
                rows={12}
                value={JSON.stringify(data, null, 2)}
                InputProps={{ readOnly: true }}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Card>
          )}

          {tab === 1 && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Typography variant="subtitle1">
                  {t('courses:jsonImportExport.import.title')}
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<FileUploadIcon />}
                  size="medium"
                >
                  {t('courses:jsonImportExport.import.uploadButton')}
                  <input
                    type="file"
                    accept=".json"
                    hidden
                    onChange={handleUpload}
                  />
                </Button>
                {jsonStructure && (
                  <Tooltip
                    title={t('courses:jsonImportExport.import.structureTooltip')}
                  >
                    <IconButton onClick={toggleStructure}>
                      <HelpIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                multiline
                fullWidth
                rows={12}
                value={jsonText}
                onChange={(e) => {
                  if (error) setError('');
                  setJsonText(e.target.value);
                }}
                variant="outlined"
                error={!!error}
                placeholder={t('courses:jsonImportExport.import.placeholder')}
                sx={{ mb: 1 }}
              />
              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}

              {showStructure && jsonStructure && (
                <Card sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    {t('courses:jsonImportExport.import.structureTitle')}
                  </Typography>

                  <TextField
                    multiline
                    fullWidth
                    rows={10}
                    value={JSON.stringify(jsonStructure, null, 2)}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        fontFamily: 'monospace',
                      },
                    }}
                  />
                </Card>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} variant="outlined" size="large">
            {t('common:actions.cancel')}
          </Button>
          {tab === 1 && (
            <Button
              onClick={handleImport}
              color="primary"
              variant="contained"
              size="large"
              disabled={!jsonText.trim() || !!error}
            >
              {t('courses:jsonImportExport.import.importButton')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JsonImportExport;
